import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { Setting } from '../setting.model';
import { SettingService } from '../setting.service';

interface SettingRow {
  setting: Setting;
  control: FormControl<string>;
  saving: boolean;
}

/**
 * TVG-315: admin page for editing setting values. Mirrors the feature toggles
 * page. Settings are saved explicitly rather than on every keystroke.
 */
@Component({
  selector: 'app-settings-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextareaComponent, ButtonComponent],
  templateUrl: './settings-page.component.html',
})
export class SettingsPageComponent implements OnInit {
  rows: SettingRow[] = [];
  loading = false;

  constructor(
    private readonly service: SettingService,
    private readonly notifications: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service.list().subscribe({
      next: (settings) => {
        this.rows = settings.map((setting) => this.buildRow(setting));
        this.loading = false;
      },
      error: () => {
        this.notifications.error('Failed to load settings');
        this.loading = false;
      },
    });
  }

  isDirty(row: SettingRow): boolean {
    return row.control.value !== (row.setting.value ?? '');
  }

  save(row: SettingRow): void {
    if (!this.isDirty(row) || row.saving) {
      return;
    }

    row.saving = true;
    this.service.update(row.setting.key, row.control.value).subscribe({
      next: (updated) => {
        row.setting = updated;
        row.control.setValue(updated.value ?? '', { emitEvent: false });
        row.saving = false;
        this.notifications.success(`${updated.label} saved`);
      },
      error: (err) => {
        row.saving = false;
        this.notifications.error(err?.error?.message || 'Failed to save setting');
      },
    });
  }

  revert(row: SettingRow): void {
    row.control.setValue(row.setting.value ?? '');
  }

  formatTimestamp(value: string | null): string {
    if (!value) {
      return 'Never';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${day} ${month} ${year}, ${time}`;
  }

  private buildRow(setting: Setting): SettingRow {
    return {
      setting,
      control: new FormControl<string>(setting.value ?? '', { nonNullable: true }),
      saving: false,
    };
  }
}
